import { Link } from 'react-router-dom';
import { Clock, AlertTriangle, Zap, X } from 'lucide-react';
import useSubscription from '@/hooks/useSubscription';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';

const DISMISS_KEY = 'groovlens:trial-banner-dismissed-on';

/*
 * TrialBanner
 *
 * Sticky banner that surfaces the user's trial countdown on the Collection
 * page. Severity escalates as the trial nears its end:
 *   - info     (3–7 days left): teal accent, calm tone
 *   - warning  (2 days left):    amber accent + bolder copy + Day-5 reminder
 *   - critical (≤1 day left):    coral accent + urgent "last day" framing
 *
 * Dismissal is tracked per-day, so the banner shows up once a day at most
 * after the user closes it. Critical state cannot be dismissed — if they're
 * about to lose access, we keep nagging.
 */
export default function TrialBanner() {
  const { shouldShowTrialBanner, isTrialing, daysLeft, trialSeverity } = useSubscription();
  const [dismissedToday, setDismissedToday] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(DISMISS_KEY);
      const today = new Date().toISOString().slice(0, 10);
      setDismissedToday(stored === today);
    } catch (_) {
      // ignore
    }
  }, [daysLeft]);

  if (!shouldShowTrialBanner || !isTrialing) return null;
  const critical = trialSeverity === 'critical';
  if (dismissedToday && !critical) return null;

  const handleDismiss = () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      localStorage.setItem(DISMISS_KEY, today);
      setDismissedToday(true);
    } catch (_) {
      // localStorage may be unavailable (Safari private mode, quota exceeded) — safe to ignore
    }
  };

  const palette = {
    info: {
      border: 'border-[hsl(var(--primary))]/30',
      bg: 'bg-[hsl(var(--primary))]/5',
      iconBg: 'bg-[hsl(var(--primary))]',
      iconFg: 'text-[hsl(var(--primary-foreground))]',
      Icon: Clock,
    },
    warning: {
      border: 'border-amber-500/40',
      bg: 'bg-amber-500/10',
      iconBg: 'bg-amber-500',
      iconFg: 'text-amber-950',
      Icon: AlertTriangle,
    },
    critical: {
      border: 'border-[#FF7A6A]/50',
      bg: 'bg-[#FF7A6A]/10',
      iconBg: 'bg-[#FF7A6A]',
      iconFg: 'text-[#1d0a08]',
      Icon: Zap,
    },
  }[trialSeverity];
  const Icon = palette.Icon;

  const headline = (() => {
    if (critical) return 'Last day of your free trial';
    if (trialSeverity === 'warning') return `${daysLeft} days left in your trial`;
    return `Trial — ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} remaining`;
  })();

  const subhead = (() => {
    if (critical) return 'Subscribe today to keep scanning. Your crates stay readable for 30 days after lockout.';
    if (trialSeverity === 'warning') return 'Upgrade to Pro to keep your crates and unlock unlimited scanning.';
    return 'You have full access to every feature. Choose a plan when you\u2019re ready.';
  })();

  return (
    <div
      className={`mb-4 rounded-xl border ${palette.border} ${palette.bg} px-3 py-2.5 sm:px-4 sm:py-3 flex items-start gap-3`}
      data-testid="trial-banner"
      data-severity={trialSeverity}
      role="region"
      aria-label="Trial status"
    >
      <div className={`shrink-0 w-9 h-9 rounded-lg ${palette.iconBg} grid place-items-center ${palette.iconFg}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-tight" data-testid="trial-banner-headline">
          {headline}
        </p>
        <p className="text-xs text-muted-foreground leading-tight mt-0.5" data-testid="trial-banner-subhead">
          {subhead}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Link to="/pricing">
          <Button
            size="sm"
            className="h-8 rounded-lg text-xs px-3"
            data-testid="trial-banner-upgrade-button"
          >
            {critical ? 'Subscribe now' : 'See plans'}
          </Button>
        </Link>
        {!critical && (
          <button
            type="button"
            onClick={handleDismiss}
            className="h-8 w-8 grid place-items-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label="Hide trial banner for today"
            data-testid="trial-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
